import { InfraConstants } from 'infra/infraConstants';
// @ts-ignore
import RegistrationReminder from 'services/billing/mediators/registration-reminder-mediator';
import billing from 'services/billing/service';
import csrfToken from 'system/csrfToken';
import currentUser from 'system/currentUser';
import { metrics } from 'system/libs/metrics';
import router from 'system/router';
import { CryptoHelper } from 'utils/CryptoHelper';
import { McsErrors } from 'utils/McsErrors/McsErrors';
import { ModuleListReloader } from 'utils/ModuleListReloader/ModuleListReloader';
import { reduxHelper } from 'utils/reduxHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';
import { reflectStorage } from 'utils/ReflectStorage';
import { SentryUtils } from 'utils/SentryUtils';
import { stateHelper } from 'utils/stateHelper';

import { Environment } from 'Environment';
import { AuthorisationApi } from 'reactApp/api/AuthorisationApi';
// @ts-ignore
import { BillingSelectors } from 'reactApp/modules/BillingModule/BillingModule';
import {
    CurrentUserActions,
    CurrentUserSelectors,
    CurrentUserTypes,
} from 'reactApp/modules/CurrentUserModule';
import { BillingPermissionsSelectors } from 'reactApp/modules/PermissionsModule/BillingPermissionsModule';
import { PermissionsActions } from 'reactApp/modules/PermissionsModule/PermissionsModule';
import { ProjectPermissionsSelectors } from 'reactApp/modules/PermissionsModule/ProjectPermissionsModule';
import {
    ProjectActions,
    ProjectSelectors,
} from 'reactApp/modules/ProjectModule';
import { ServiceActions } from 'reactApp/modules/ServiceModule/ServiceModule';
import { WhiteLabelSelectors } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule';

const INITIAL_STATE = {
    isAuthorisationChecked: false,
    isAuthorised: false,
};

const module = stateHelper.createModuleState('Authorisation', INITIAL_STATE);

export interface AuthorisationModuleInterface {
    Authorisation: typeof INITIAL_STATE;
}

let resolveAuthorisationCheckingPromise: Function;

export const authorisationCheckingPromise = new Promise((resolve) => {
    resolveAuthorisationCheckingPromise = resolve;
});

let wasAuthorised = false;

const checkAuthorisation = () => async (dispatch, getState) => {
    try {
        await dispatch(CurrentUserActions.loadUser());

        if (!wasAuthorised) {
            wasAuthorised = true;

            if (CurrentUserSelectors.getMustEnableTfa(getState())) {
                await dispatch(onSemiAuthorisedWhenTfaRequired());
                return false;
            }

            await dispatch(onAuthorised());
        }

        return true;
    } catch (error) {
        if (Number(error.status) === 401) {
            wasAuthorised = false;
            dispatch(module.mergeModuleState({ isAuthorised: false }));
        }
        return getIsAuthorised(getState());
    } finally {
        dispatch(module.setPart('isAuthorisationChecked', true));
        resolveAuthorisationCheckingPromise();
    }
};

const { addConsumer: addAuthCheckingConsumer } = new ModuleListReloader({
    loadList: checkAuthorisation,
    updateTimeout: InfraConstants.LONG_UPDATE_INTERVAL,
});

const signIn = (
    data: CurrentUserTypes.SignInSignUpData,
    errorHandlingDisabled?: boolean,
): ReduxTypeUtils.ThunkResult<Promise<
    CurrentUserTypes.UserApiEntity
>> => async (dispatch, getState) => {
    const user = await dispatch(
        CurrentUserActions.signIn(data, errorHandlingDisabled),
    );

    const isTfaRequired = CurrentUserSelectors.getIsTfaRequired(getState());
    const isTfaEnabled = CurrentUserSelectors.getIsTfaEnabled(getState());

    if (isTfaEnabled) {
        await dispatch(onSemiAuthorisedWhenTfaEnabled());
        throw { __tfaEnabled: true };
    }

    if (isTfaRequired) {
        await dispatch(onSemiAuthorisedWhenTfaRequired());
    } else {
        await dispatch(onAuthorised());
    }

    return user;
};

const authTfa = (
    code: string,
): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch) => {
    try {
        await AuthorisationApi.authTfa(code);

        await dispatch(onAuthorised());
    } catch (errorResponse) {
        const responseParsed = McsErrors.transformErrorResponseKey(
            errorResponse,
            {
                tfa_code: 'code',
                tfa: 'code',
            },
        );
        throw McsErrors.getFormMessagesFromResponse(responseParsed, {
            fieldTexts: {
                default: 'Введено некорректное значение',
                code: {
                    argument_invalid: 'Введен неверный код',
                    unauthorized: 'Введен неверный код',
                    blocked: `Аккаунт заблокирован. ${McsErrors.CALL_SUPPORT_HTML}`,
                },
            },
            commonTexts: {
                blocked: `Аккаунт заблокирован. ${McsErrors.CALL_SUPPORT_HTML}`,
                default: `Не удалось выполнить авторизацию. ${McsErrors.TRY_LATER_HTML}`,
            },
        });
    }
};

const signUp = (
    data:
        | CurrentUserTypes.SignInSignUpData
        | CurrentUserTypes.PartnerSignupData,
    params?: CurrentUserTypes.SignUpParams,
): ReduxTypeUtils.ThunkResult<Promise<
    CurrentUserTypes.UserApiEntity
>> => async (dispatch, getState) => {
    const user = await dispatch(CurrentUserActions.signUp(data, params));

    metrics.send({
        category: 'lead',
        action: 'registration',
        label: user.projects[0].pid,
    });

    const isTfaRequired = CurrentUserSelectors.getIsTfaRequired(getState());
    if (isTfaRequired) {
        await dispatch(onSemiAuthorisedWhenTfaRequired());
    } else {
        await dispatch(onAuthorised());
    }

    return user;
};

const onSemiAuthorisedWhenTfaEnabled = (): ReduxTypeUtils.ThunkResult<Promise<
    void
>> => async (dispatch, getState) => {
    currentUser.set('email', CurrentUserSelectors.getEmail(getState()));

    await csrfToken.updateFromUserData({
        email: CurrentUserSelectors.getEmail(getState()),
    });
};

const signOutWhenSemiAuthorisedTfaEnabled = () => async (dispatch) => {
    await dispatch(signOut({ avoidPageReload: true }));
};

const onSemiAuthorisedWhenTfaRequired = (): ReduxTypeUtils.ThunkResult<Promise<
    void
>> => async (dispatch, getState) => {
    SentryUtils.setUser(
        CurrentUserSelectors.getModuleState(getState()) as any,
        CurrentUserSelectors.getPid(getState()),
    );
    await csrfToken.updateFromUserData({
        email: CurrentUserSelectors.getEmail(getState()),
    });

    dispatch(module.mergeModuleState({ isAuthorised: true }));
};

const onAuthorised = (): ReduxTypeUtils.ThunkResult<Promise<void>> => async (
    dispatch,
    getState,
) => {
    // order is important
    dispatch(CurrentUserActions.setModuleDataToCurrentUserModel());

    await csrfToken.updateFromUserData({
        email: CurrentUserSelectors.getEmail(getState()),
    });
    dispatch(setPhone());
    dispatch(redirectToPartnerAppIfHasOnlyPartnerProjects());

    const pid = CurrentUserSelectors.getPid(getState());

    SentryUtils.setUser(
        CurrentUserSelectors.getModuleState(getState()) as any,
        pid,
    );

    if (pid) {
        await Promise.all([
            dispatch(ProjectActions.loadData(pid)),
            dispatch(PermissionsActions.loadPermissions(pid)),
        ]);

        const isProjectEnabled = ProjectSelectors.getIsProjectEnabled(
            getState(),
        );
        const isUserEnabled = CurrentUserSelectors.getIsUserEnabled(getState());

        if (
            !Environment.isLandings &&
            !router.isPartnerApp() &&
            isProjectEnabled &&
            isUserEnabled
        ) {
            await dispatch(loadServicesAndBillingIfVerified());
        }

        dispatch(showRegReminder());
    }

    dispatch(module.mergeModuleState({ isAuthorised: true }));
};

const setPhone = () => async (dispatch, getState) => {
    const phoneEncrypted = reflectStorage.get<string>('signupPhone');
    if (!phoneEncrypted) {
        return;
    }
    const email = CurrentUserSelectors.getEmail(getState()) || '';
    const phoneDecrypted = CryptoHelper.decrypt(phoneEncrypted, email);

    if (phoneDecrypted) {
        await dispatch(CurrentUserActions.setPhone(phoneDecrypted));
        reflectStorage.remove('signupPhone');
    }
};

const showRegReminder = () => (dispatch, getState) => {
    if (Environment.isPrivateBuild) {
        return false;
    }

    const permissions = ProjectPermissionsSelectors.getPermissions(getState());
    const isBillingLoaded = BillingSelectors.getIsLoaded(getState());
    const isBillingEnabled = BillingPermissionsSelectors.getCanViewService(
        getState(),
    );

    if (permissions.modifyProject && isBillingLoaded && isBillingEnabled) {
        // @ts-ignore
        // eslint-disable-next-line
        new RegistrationReminder({
            balanceModel: billing.getModel(),
        });
    }
};

const loadServicesAndBillingIfVerified = () => async (dispatch, getState) => {
    const isBillingEnabled = BillingPermissionsSelectors.getCanViewService(
        getState(),
    );

    if (CurrentUserSelectors.getIsVerified(getState())) {
        const pid = CurrentUserSelectors.getPid(getState());
        return Promise.all([
            dispatch(ServiceActions.addConsumer(pid)),
            isBillingEnabled &&
                billing.load().catch(() => {
                    // it's ok
                }),
        ]);
    }
};

const redirectToPartnerAppIfHasOnlyPartnerProjects = () => (
    dispatch,
    getState,
) => {
    const regularProjects = CurrentUserSelectors.getRegularProjects(getState());
    const partnerProjects = CurrentUserSelectors.getPartnerProjects(getState());
    if (
        !Environment.isLandings &&
        !router.isPartnerApp() &&
        partnerProjects.length &&
        !regularProjects.length
    ) {
        window.location.href = '/partner/app';
        throw 'redirect';
    }
};

const authoriseOnRequiredTfaEnabled = onAuthorised;

const authoriseOnRequiredTfaDisabled = () => async () => {
    window.location.reload();
    // currentUser.resetToUnauthorisedValues();
};

// need this for toolkit email verification page
const markEmailVerified = (): ReduxTypeUtils.ThunkResult<Promise<
    void
>> => async (dispatch) => {
    dispatch(CurrentUserActions.setIsEmailVerified(true));
    dispatch(CurrentUserActions.setIsFirstLoginAfterEmailConfirmation());
    dispatch(CurrentUserActions.setIsShowPhoneVerificationDialog(true));

    currentUser.set('verified', true);

    dispatch(setPhone());

    await dispatch(loadServicesAndBillingIfVerified());
};

const signOut = ({
    avoidPageReload,
}: {
    avoidPageReload?: boolean;
} = {}): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch) => {
    const hasWhiteLabelSso = WhiteLabelSelectors.getHasWhiteLabelSso();
    if (hasWhiteLabelSso) {
        return window.location.replace(
            window.location.origin + '/api/v1/auth/oidc/logout',
        );
    }

    try {
        await dispatch(CurrentUserActions.signOut());
    } catch (errorResponse) {
        // nothing to do
    } finally {
        currentUser.set('email', 'anonym');
        dispatch(CurrentUserActions.removeInfraToken());
        dispatch(module.mergeModuleState({ isAuthorised: false }));
        if (!avoidPageReload) {
            window.location.href = '/';
        }
    }
};

export const AuthorisationActions = {
    checkAuthorisation,
    addAuthCheckingConsumer,
    signIn,
    authTfa,
    signUp,
    signOut,
    signOutWhenSemiAuthorisedTfaEnabled,
    markEmailVerified,
    authoriseOnRequiredTfaEnabled,
    authoriseOnRequiredTfaDisabled,
};

const getIsAuthorisationChecked = module.makePartGetter(
    'isAuthorisationChecked',
);
const getIsAuthorised = module.makePartGetter('isAuthorised');

export const AuthorisationSelectors = {
    getIsAuthorisationChecked,
    getIsAuthorised,
};

export const BoundAuthorisationActions = reduxHelper.bindActions(
    AuthorisationActions,
);
export const BoundAuthorisationSelectors = reduxHelper.bindSelectors<
    typeof AuthorisationSelectors
>(AuthorisationSelectors);
